import React, { useState, useEffect } from 'react';
import moment from 'moment';
import styled from 'styled-components';

import {
  SelectField,
  TextField,
  PhoneField,
  TextareaField,
  CheckboxField,
  TimeField,
  DropzoneField,
  TagField,
  InfoField,
} from '~fields';

import { Gap } from '~ui-kit';
import NoticeBubbles from './NoticeBubbles';

const severityOptions = [
  { label: 'Matala', value: 'LOW' },
  { label: 'Vakava', value: 'SEVERE' },
  { label: 'Kriittinen', value: 'CRITICAL' },
];

// Lataa liitteitä (tiedoston muoto: jpg, png, pdf, xlsx, xls, doc, docx)
const attachmentAccept = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
  'application/vnd.ms-excel': ['.xls'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
};

const requestTypeOptions = [
  {
    label: 'Häiriö',
    value: 'FAULT',
  },
  {
    label: 'Palvelupyyntö',
    value: 'REQUEST',
  },
];

const renderExtraField = field => {
  const sharedProps = {
    key: field.field,
    label: field.label || field.Label,
    placeholder: field.placeholder,
    info: field.info,
    required: field.required,
  };

  switch (field.field) {
    case 'socialSecurityNumber':
      return <TextField {...sharedProps} name="socialSecurityNumber" />;
    case 'pcNumber':
      return <TextField {...sharedProps} name="pcNumber" />;
    case 'systemCaseId':
      return <TextField {...sharedProps} name="systemCaseId" />;
    case 'subsystem':
      return <TextField {...sharedProps} name="subsystem" />;
    case 'departmentCode':
      return <TextField {...sharedProps} name="departmentCode" />;
    case 'severity':
      return (
        <>
          <Gap height="16px" />
          <TagField
            {...sharedProps}
            name="severity"
            options={severityOptions}
          />
        </>
      );
    case 'othersHaveSameProblem':
      return <CheckboxField {...sharedProps} name="othersHaveSameProblem" />;
    case 'errorCode':
      return <TextField {...sharedProps} name="errorCode" />;
    case 'time':
      return (
        <TimeField {...sharedProps} name="time" timeIntervals={30} required />
      );
    case 'problemDescription':
      return (
        <TextareaField {...sharedProps} name="problemDescription" required />
      );
    default:
      return null;
  }
};

const ICTTroubleForm = props => {
  const {
    systems,
    setExternalValidations,
    formik: { setFieldValue, values },
  } = props;

  const { requestType } = values;

  const [systemMeta, setSystemMeta] = useState();

  const systemOptions = systems.map(s => ({ value: s.id, label: s.name }));
  // Only allow request types that are explicitly allowed by the system
  const restrictedRequestTypeOptions = requestTypeOptions.map(option => ({
    ...option,
    disabled: !systemMeta?.requestTypes.includes(option.value),
  }));

  const updateSystemMeta = system => {
    if (system) {
      const systemMeta = systems.find(s => s.id === system.value);
      setSystemMeta(systemMeta);

      const filteredRequestTypeOptions = requestTypeOptions.filter(option =>
        systemMeta.requestTypes.includes(option.value)
      );

      // If only one request type is available, set it as default
      // otherwise make the user choose
      if (filteredRequestTypeOptions.length === 1) {
        setFieldValue('requestType', filteredRequestTypeOptions[0].value);
      } else {
        setFieldValue('requestType', null);
      }
    } else {
      setSystemMeta(null);
    }

    // Reset time
    setFieldValue('time', moment());
  };

  const fields = systemMeta?.fields[requestType] || [];
  const upperFields = [];
  const lowerFields = [];

  fields.forEach(field => {
    if (field.field === 'subsystem') {
      upperFields.push(field);
    } else {
      lowerFields.push(field);
    }
  });

  // Add new validations based on system + request type combo
  // This is easiest to reason about by using a side effect imo
  useEffect(() => {
    if (systemMeta && requestType) {
      const newValidations = systemMeta.fields[requestType]
        .map(field => {
          if (field.required) {
            return {
              name: field.field,
              getError: value => (value ? undefined : 'Vaadittu'),
            };
          }
          return null;
        })
        .filter(Boolean); // Filter out nulls
      setExternalValidations(newValidations);
    } else {
      setExternalValidations([]);
    }
  }, [systemMeta, requestType]);

  return (
    <FormWrapper>
      <SelectField
        name="system"
        options={systemOptions}
        onChange={updateSystemMeta}
        required
      />
      {systemMeta && <NoticeBubbles content={systemMeta.contactInfo} />}
      <InfoField name="systemInfo" />
      {systemMeta && (
        <TagField name="requestType" options={restrictedRequestTypeOptions} />
      )}
      {systemMeta && requestType && (
        <>
          {upperFields.map(renderExtraField)}
          <TextField name="topic" required />
          {lowerFields.map(renderExtraField)}
          <DropzoneField name="attachmentInput" accept={attachmentAccept} />
          <Gap height="16px" />
          <PhoneField name="contactPhone" required />
        </>
      )}
    </FormWrapper>
  );
};

const FormWrapper = styled.div`
  & > * {
    margin-bottom: 24px;
  }
`;

export default ICTTroubleForm;
